import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    X, Sparkles, Wand2, Heart, Ruler, Truck, ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FirstLoginWelcome — one-time bento-grid welcome shown right after a user's
 * very first successful sign-in (any auth method). Mounted globally inside
 * AuthContext; auto-shows when /auth/me returns needs_welcome=true and
 * marks itself seen via POST /auth/welcome-seen on dismiss.
 *
 * Layout: 12-column asymmetric bento on desktop, single-column scroll on
 * mobile. Tiles stagger in with a slow "elegant-fade-up" (defined in
 * tailwind.config.js) so the surface settles like a couture lookbook rather
 * than springs in. Every photographic tile has a Ken-Burns idle hover.
 */
export default function FirstLoginWelcome({ open, onClose, userName = "" }) {
    const navigate = useNavigate();
    const [dismissing, setDismissing] = useState(false);
    // iter231 — Respect the master Custom Studio flag. When admin turns it
    // off via /config/custom-studio/settings, the welcome tile + "Begin a
    // design" CTA must NOT appear (was showing unconditionally → bug
    // reported by user in both preview + prod).
    const [studioEnabled, setStudioEnabled] = useState(false);
    useEffect(() => {
        if (!open) return;
        axios.get(`${API}/custom-studio/state`)
            .then(r => setStudioEnabled(!!r.data?.enabled))
            .catch(() => setStudioEnabled(false));
    }, [open]);

    // Mark as seen — fire-and-forget so a flaky network never blocks UX.
    const markSeen = () => {
        try { axios.post(`${API}/auth/welcome-seen`, {}, { withCredentials: true }); } catch { /* noop */ }
    };

    const handleSkip = () => {
        if (dismissing) return;
        setDismissing(true);
        markSeen();
        // Tiny delay so the exit animation can play before unmount.
        setTimeout(() => onClose?.(), 200);
    };

    const handleStart = () => {
        if (dismissing) return;
        setDismissing(true);
        markSeen();
        setTimeout(() => { onClose?.(); navigate("/"); }, 200);
    };

    // Esc to close
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") handleSkip(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Lock background scroll while open
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    if (!open) return null;

    const firstName = (userName || "").trim().split(" ")[0] || "";

    return createPortal(
        <div
            className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6 bg-ink/55 backdrop-blur-md animate-in fade-in duration-300"
            data-testid="welcome-modal"
            onClick={handleSkip}
            role="dialog"
            aria-modal="true"
            aria-labelledby="welcome-modal-title"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className={`relative w-full max-w-6xl max-h-[94vh] overflow-y-auto bg-[#FAF6F1] rounded-2xl sm:rounded-3xl shadow-2xl border border-[#C9A47A]/20 p-5 sm:p-10 ${dismissing ? "opacity-0 scale-[0.98] transition-all duration-200" : "animate-in zoom-in-95 duration-500"}`}
            >
                {/* Close */}
                <button
                    onClick={handleSkip}
                    aria-label="Close welcome"
                    data-testid="welcome-modal-close"
                    className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 w-9 h-9 rounded-full inline-flex items-center justify-center text-ink/50 hover:text-ink hover:bg-ink/5 transition-colors"
                >
                    <X size={18} />
                </button>

                {/* Hero */}
                <div className="text-center mb-8 sm:mb-12 px-2 opacity-0 animate-elegant-fade-up" style={{ animationDelay: "0ms" }}>
                    <img
                        src="/jns-logo.png"
                        alt="JnS Creation"
                        className="h-12 sm:h-14 w-auto object-contain mx-auto mb-3"
                    />
                    <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.4em] text-[#C9A47A] mb-3">
                        Welcome to the boutique
                    </p>
                    <h1
                        id="welcome-modal-title"
                        className="font-display text-3xl sm:text-5xl text-ink leading-tight tracking-tight"
                    >
                        {firstName ? `Hello, ${firstName} ✦` : "Hello, lovely ✦"}
                    </h1>
                    <p className="font-sans text-sm sm:text-base text-ink/65 max-w-xl mx-auto mt-3 sm:mt-4 leading-relaxed">
                        Your bespoke ethnic journey begins here.{" "}
                        Here's what's been laid out for you at JnS Creation —
                    </p>
                </div>

                {/* Bento grid — 12-col asymmetric on desktop, stacks on mobile */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-5 auto-rows-auto lg:auto-rows-[minmax(140px,auto)]">

                    {/* TILE 1 — AI Custom Studio (PRIMARY, tall hero)
                        iter231 — Only render when the master Custom Studio
                        flag is on. When admin turns it off, the tile is
                        hidden + Tile 2 expands to fill the grid. */}
                    {studioEnabled && (
                    <BentoTile
                        spans="lg:col-span-5 lg:row-span-2 min-h-[260px] sm:min-h-[340px] lg:min-h-[420px]"
                        delay={100}
                        testid="welcome-tile-custom"
                        onClick={() => { markSeen(); onClose?.(); navigate("/custom-studio"); }}
                    >
                        <img
                            src="https://images.unsplash.com/photo-1617055407123-3d7130c1f940?w=1200&q=85&auto=format&fit=crop"
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1800ms] ease-out"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/45 to-transparent" />
                        <div className="relative h-full flex flex-col justify-end p-5 sm:p-7 text-ivory">
                            <div className="inline-flex items-center gap-1.5 mb-3 self-start">
                                <Sparkles size={13} className="text-[#E5C99B]" />
                                <span className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#E5C99B]">
                                    AI Custom Studio
                                </span>
                            </div>
                            <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-[1.05] tracking-tight mb-2">
                                Design your own,<br />stitch by stitch.
                            </h3>
                            <p className="text-sm text-ivory/80 leading-relaxed max-w-md">
                                Pick the fabric, the silhouette, the embroidery. Our AI renders the outfit on you before a single stitch is made.
                            </p>
                            <span className="inline-flex items-center gap-1.5 mt-4 text-xs font-bold uppercase tracking-wider text-ivory border-b border-ivory/40 self-start pb-0.5 group-hover:gap-3 transition-all">
                                Begin a design <ArrowRight size={13} />
                            </span>
                        </div>
                    </BentoTile>
                    )}

                    {/* TILE 2 — Readymade (PRIMARY)
                        iter231 — Expand to full row when the custom tile
                        is hidden so the grid stays balanced. */}
                    <BentoTile
                        spans={studioEnabled
                            ? "lg:col-span-7 lg:row-span-1 min-h-[200px] sm:min-h-[240px]"
                            : "lg:col-span-12 lg:row-span-1 min-h-[200px] sm:min-h-[240px]"}
                        delay={250}
                        testid="welcome-tile-readymade"
                        onClick={() => { markSeen(); onClose?.(); navigate("/"); }}
                    >
                        <img
                            src="https://images.pexels.com/photos/13124449/pexels-photo-13124449.jpeg?w=1400&q=85&auto=compress&cs=tinysrgb"
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1800ms] ease-out"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/45 to-ink/10" />
                        <div className="relative h-full flex flex-col justify-end p-5 sm:p-7 text-ivory">
                            <span className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#E5C99B] mb-2">
                                Readymade Boutique
                            </span>
                            <h3 className="font-display text-2xl sm:text-3xl leading-[1.1] tracking-tight max-w-md">
                                Hand-picked drapes, ready to wear.
                            </h3>
                            <p className="text-sm text-ivory/80 mt-1.5 max-w-sm">
                                Curated sarees, kurtas & lehengas in stock — shipped within days.
                            </p>
                        </div>
                    </BentoTile>

                    {/* TILE 3 — Virtual Try-On (FEATURED, light tile) */}
                    <BentoTile
                        spans="lg:col-span-4 lg:row-span-1 min-h-[200px] sm:min-h-[240px] bg-[#F1E8DD]"
                        delay={400}
                        testid="welcome-tile-tryon"
                        onClick={() => { markSeen(); onClose?.(); navigate("/"); }}
                        light
                    >
                        <div className="relative h-full p-5 sm:p-7 flex flex-col justify-between">
                            <div>
                                <div className="inline-flex items-center gap-1.5 mb-3">
                                    <span className="w-10 h-10 rounded-full bg-white/70 inline-flex items-center justify-center text-[#C9A47A] group-hover:-translate-y-1 transition-transform duration-500 shadow-sm">
                                        <Wand2 size={18} />
                                    </span>
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#9C7A4F] block mb-2">
                                    Virtual Try-On
                                </span>
                                <h3 className="font-heading text-lg sm:text-xl text-ink leading-tight">
                                    See it on you,<br />before you say yes.
                                </h3>
                            </div>
                            <p className="text-[12px] text-ink/65 leading-relaxed">
                                Upload a selfie. Our AI drapes any piece on you — instantly.
                            </p>
                        </div>
                    </BentoTile>

                    {/* TILE 4 — Saved Measurements (SECONDARY, photo+scrim) */}
                    <BentoTile
                        spans="lg:col-span-3 lg:row-span-1 min-h-[160px] sm:min-h-[180px]"
                        delay={550}
                        testid="welcome-tile-measurements"
                        onClick={() => { markSeen(); onClose?.(); navigate("/profile"); }}
                    >
                        <img
                            src="https://images.pexels.com/photos/7778194/pexels-photo-7778194.jpeg?w=600&q=85&auto=compress&cs=tinysrgb"
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-[1.04] group-hover:opacity-100 transition-all duration-[1800ms] ease-out"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-[#FAF6F1]/82 group-hover:bg-[#FAF6F1]/72 transition-colors duration-500" />
                        <div className="relative h-full p-5 flex flex-col justify-between">
                            <Ruler size={20} className="text-[#C9A47A]" />
                            <div>
                                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#9C7A4F] block mb-1">
                                    Saved Measurements
                                </span>
                                <p className="font-heading text-base text-ink leading-tight">
                                    One fit profile,<br />every order.
                                </p>
                            </div>
                        </div>
                    </BentoTile>

                    {/* TILE 5 — Door-to-door Tracking (SECONDARY) */}
                    <BentoTile
                        spans="lg:col-span-5 lg:row-span-1 min-h-[160px] sm:min-h-[180px] bg-white"
                        delay={700}
                        testid="welcome-tile-tracking"
                        onClick={() => { markSeen(); onClose?.(); navigate("/orders"); }}
                        light
                    >
                        <div className="relative h-full p-5 sm:p-6 flex items-center gap-5">
                            <span className="shrink-0 w-12 h-12 rounded-full bg-[#F1E8DD] inline-flex items-center justify-center text-[#C9A47A] group-hover:-translate-y-1 transition-transform duration-500 shadow-sm">
                                <Truck size={20} />
                            </span>
                            <div className="min-w-0">
                                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#9C7A4F] block mb-1">
                                    Door-to-door tracking
                                </span>
                                <p className="font-heading text-base sm:text-lg text-ink leading-tight">
                                    Watch your piece come to life
                                </p>
                                <p className="text-[11px] text-ink/55 mt-1">
                                    Stitching progress · shipping updates · delivery photos.
                                </p>
                            </div>
                        </div>
                    </BentoTile>

                    {/* TILE 6 — Wishlist (SECONDARY) */}
                    <BentoTile
                        spans="lg:col-span-4 lg:row-span-1 min-h-[160px] sm:min-h-[180px] bg-[#F1E8DD]"
                        delay={850}
                        testid="welcome-tile-wishlist"
                        onClick={() => { markSeen(); onClose?.(); navigate("/wishlist"); }}
                        light
                    >
                        <div className="relative h-full p-5 sm:p-6 flex items-center justify-between">
                            <div className="min-w-0">
                                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#9C7A4F] block mb-1">
                                    Wishlist
                                </span>
                                <p className="font-heading text-base sm:text-lg text-ink leading-tight">
                                    Heart it. Come back to it.
                                </p>
                                <p className="text-[11px] text-ink/55 mt-1">
                                    Curate your own boutique shelf.
                                </p>
                            </div>
                            <Heart
                                size={48}
                                strokeWidth={1.25}
                                className="shrink-0 text-[#C9A47A] opacity-30 group-hover:opacity-60 group-hover:scale-105 transition-all duration-500 -mr-2"
                                fill="currentColor"
                            />
                        </div>
                    </BentoTile>
                </div>

                {/* Footer — Skip + Start exploring */}
                <div
                    className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4 mt-8 sm:mt-10 pt-6 border-t border-[#C9A47A]/20 opacity-0 animate-elegant-fade-up"
                    style={{ animationDelay: "1000ms" }}
                >
                    <button
                        type="button"
                        onClick={handleSkip}
                        data-testid="welcome-skip-btn"
                        className="text-sm text-ink/55 hover:text-ink underline-offset-4 hover:underline transition-colors px-2 py-2"
                    >
                        Skip for now
                    </button>
                    <button
                        type="button"
                        onClick={handleStart}
                        data-testid="welcome-start-btn"
                        className="group inline-flex items-center justify-center gap-2 bg-ink text-ivory hover:bg-ink/90 font-sans text-sm font-bold uppercase tracking-wider px-8 py-3.5 rounded-md transition-all duration-300 shadow-md hover:shadow-xl w-full sm:w-auto"
                    >
                        Start exploring
                        <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}


/* ------------- Inner shared tile ------------- */
function BentoTile({ spans, delay, children, onClick, testid, light }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            style={{ animationDelay: `${delay}ms` }}
            className={`group relative overflow-hidden rounded-xl sm:rounded-2xl text-left
                border ${light ? "border-[#C9A47A]/15" : "border-transparent"}
                hover:border-[#C9A47A]/35 hover:shadow-xl
                transition-all duration-500
                opacity-0 animate-elegant-fade-up
                motion-reduce:animate-none motion-reduce:opacity-100
                focus:outline-none focus:ring-2 focus:ring-[#C9A47A]/40 focus:ring-offset-2 focus:ring-offset-[#FAF6F1]
                ${spans}`}
        >
            {children}
        </button>
    );
}
